import { fetchFromGraphql } from 'entities/helpers/gqlCall';
import Api from 'entities/helpers/api';
import ErrorLogger from 'entities/helpers/error-log-helper';
import homepageQueries from 'apps/home/utils/gql/queries';

// Get the number of days relevant for each section
// We should probably move this to the API
const getNumberOfDaysRelevantForSection = (section) => {
  switch (section) {
    case 'recent_gift':
      return 15;
    case 'gift_anniversary':
      return 30;
    case 'birthday':
      return 7;
    case 'career_move':
      return 120;
    case 'trip_meeting':
      return 45;
    case 'dps':
      return 30;
    default:
      return null;
  }
};

// Fetch recommended actions for the user that have already been created
// Returns an array of objects with a "segment" key and an "actions" key
// Example: [{ segment: 'recent_gift', actions: [{ id: 1, ... }] }, { segment: 'career_move', actions: [{ id: 2, ... }] }]
export const getExistingRecommendedActions = async (segments) => {
  const query = homepageQueries.fetchRecommendedActionsBySegment;
  const params = { segments, includeViewed: false };
  const { recommendedActions } = await fetchFromGraphql(query, params, 'recommendedActions');
  return recommendedActions || [];
};

// We use this to check to see if there are any new contacts to create recommended actions for
// Returns an array of objects with a "segment" key and a "contactIds" key
// Example: [{ segment: 'recent_gift', contactIds: [1, 2] }, { segment: 'career_move', contactIds: [3] }]
export const getContactIdsForSections = async (segments, userSolicitorContactId, userSettings) => {
  const { daysSinceLastGift, giftAmount, monthsSinceLastInteraction } = userSettings;
  const query = homepageQueries.fetchContactIdsForRecommendedActions;
  const params = {
    segments,
    userSolicitorContactId,
    daysSinceLastGift,
    minGiftAmount: giftAmount,
    monthsSinceLastInteraction,
  };
  const { contactIdsForNewRecommendedActions } = await fetchFromGraphql(
    query,
    params,
    'contactIdsForNewRecommendedActions'
  );
  return contactIdsForNewRecommendedActions || [];
};

// Create recommended actions for an array of contacts in a given segment
// Return an array of IDs of created recommended actions
// NOTE: the IDs are recommended_action IDs, not contact IDs
const createRecommendedActionsForContacts = async (contactIds = [], segment) => {
  const number_of_days_relevant = getNumberOfDaysRelevantForSection(segment);

  if (!contactIds.length) return [];

  try {
    const promises = contactIds.map(async (id) => {
      const payload = { contact_id: id, segment };
      return Api.JOURNEYS.RECOMMENDED_ACTIONS.post({
        data: JSON.stringify(payload),
        params: { number_of_days_relevant },
      });
    });

    const results = await Promise.allSettled(promises);
    const createdIds = results
      .filter((result) => result.status === 'fulfilled')
      .filter((ra) => !ra.value.viewed)
      .map((result) => result.value.id);

    return createdIds;
  } catch (error) {
    ErrorLogger.error('API Error when attempting to create Recommended Action: ', error);
    return [];
  }
};

// Find Contact IDs for each segment and create recommended actions for them
// Return an object with segment names as keys and arrays of created action IDs as values
// Example: { recent_gift: [1, 2], career_move: [3] }
// If no new actions are created, return an empty object
export const createNewRecommendedActions = async (sections, userSolicitorContactId, userSettings, actions) => {
  const results = {};
  const contactIdsForSections = await getContactIdsForSections(sections, userSolicitorContactId, userSettings);

  for (const group of contactIdsForSections) {
    const segment = group?.segment;
    const contactIds = group?.contactIds || [];
    const contactIdsToCreate = filterExistingContacts(contactIds, actions[segment] || []);
    if (contactIdsToCreate.length > 0) {
      const newActionIds = await createRecommendedActionsForContacts(contactIdsToCreate, segment);
      const newActions = await fetchRecommendedActionsByIds(newActionIds);
      actions[segment] = [...newActions, ...actions[segment]];
    }
  }

  return results;
};

const filterExistingContacts = (ids, existingActions) => {
  const existingActionContactIds = existingActions.map((action) => action.contact_id);
  return ids.filter((id) => !existingActionContactIds.includes(id));
};

const fetchRecommendedActionsByIds = async (ids) => {
  const query = homepageQueries.fetchRecommendedActionsByIds;
  const params = { ids };
  const { recommendedActionsById } = await fetchFromGraphql(query, params, 'recommendedActionsById');
  return recommendedActionsById || [];
};
